import React, { useState, useEffect, useRef, useContext } from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Toolbar } from 'primereact/toolbar'
import { Button } from 'primereact/button'

import server from '../../../server'
import { useLocation, useNavigate } from 'react-router-dom'
import MainContentLayout from '../../Layout/MainContentLayout'

import {
  InputFieldTemplate,
  MultiSelectTemplate,
} from '../../shared/FilterTemplates'

// Import system logos
import originsDirect from '../../../assets/images/ORIGINS-DIRECT.png'
import originsManganegment from '../../../assets/images/ORIGINS-MANAGEMENT.png'
import originsDirectInstant from '../../../assets/images/ORIGINS-DIRECT-INSTANT.png'

// import ModifyMolecule from '../Molecules/ModifyMolecule'
import ModifyManufacture from './ModifyManufacturers'
import { Toast } from 'primereact/toast'
import { formatCurrency, ToastPopUp } from '../../shared/utils'
import { AuthenticationContext } from '../../../Auth/authentication.context'
import useTableRowsPerPage from '../../../hooks/useTableRowsPerPage'
import {
  getInitialPaginationState,
  MANUFACTURERS_TABLE_ROWS_OPTIONS,
} from '../../../shared/tablePagination'

export default function ManufacturersTable() {
  const { user } = useContext(AuthenticationContext)
  const { defaultRows, rowsPerPageOptions, applyPageEvent } = useTableRowsPerPage(
    MANUFACTURERS_TABLE_ROWS_OPTIONS
  )
  const [manufacturers, setManufacturers] = useState(null)
  const [selectedMolecule, setSelectedMolecule] = useState(null)
  const [totalRecords, setTotalRecords] = useState(0)
  const [tableLoading, setTableLoading] = useState(false)
  const [csvLoading, setcsvLoading] = useState(false)
  const [clearBtnDisabled, setClearBtnDisabled] = useState(true)
  const [lazyState, setLazyState] = useState(() =>
    getInitialPaginationState(user, {
      allowedRowsOptions: MANUFACTURERS_TABLE_ROWS_OPTIONS,
      sortField: '',
      sortOrder: '',
      filters: {
        name: { value: '' },
        source: { value: '' },
        drugIdentificationNo: { value: '' },
      },
    })
  )

  useEffect(() => {
    setLazyState((prev) => ({ ...prev, rows: defaultRows }))
  }, [defaultRows])

  const initLazyState = () => {
    setLazyState((prevState) => ({
      ...prevState,
      sortField: '',
      sortOrder: '',
      filters: {
        name: { value: '' },
        source: { value: '' },
        drugIdentificationNo: { value: '' },
      },
    }))
  }

  const navigate = useNavigate()

  const dt = useRef()
  const toast = useRef()
  const { state } = useLocation()
  const AddEditRef = useRef()
  const DeleteRef = useRef()
  const refs = { AddEditRef, DeleteRef }

  const formatTimeTo12Hours = (timeString) => {
    const time = new Date(`1970-01-01T${timeString}`)
    return time.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    })
  }
  // Gets table data
  const getData = () => {
    const manufacturerName = lazyState?.filters?.name.value

    const requestParams =
      `?ordable=true` +
      `&page=${lazyState?.page ? lazyState.page + 1 : 1}` +
      `&limit=${lazyState?.rows}` +
      `${manufacturerName ? '&name=' + manufacturerName : ''}` +
      `${lazyState?.sortField ? '&sortByField=' + lazyState.sortField : ''}` +
      `${lazyState?.sortOrder ? '&sortByOrder=' + lazyState.sortOrder : ''}`

    setTableLoading(true)

    server
      .get(`/manufacturers/${requestParams}`)
      .then((response) => {
        const manufacturers = response?.data?.Manufacturers

        const formattedManufacturers = manufacturers.map((manufacturer) => ({
          ...manufacturer,
          cutOffTime: formatTimeTo12Hours(manufacturer.cutOffTime),
        }))
        setManufacturers(formattedManufacturers)
        setTotalRecords(response.data.totalCount)
        setTableLoading(false)
        // setError(null)
      })
      .catch((error) => {
        setTableLoading(false)
        // setError(error.response.data.message)
      })
  }
  const exportCSV = () => {
    const manufacturerName = lazyState?.filters?.name.value

    const requestParams =
      `?ordable=true` +
      `${manufacturerName ? '&name=' + manufacturerName : ''}` +
      `${lazyState?.sortField ? '&sortByField=' + lazyState.sortField : ''}` +
      `${lazyState?.sortOrder ? '&sortByOrder=' + lazyState.sortOrder : ''}`
    setcsvLoading(true)
    server
      .get(`/manufacturers/export/${requestParams}`, {
        responseType: 'blob',
      })
      .then((response) => {
        const blobData = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
        const url = window.URL.createObjectURL(blobData)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'manufacturers.xlsx')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        setcsvLoading(false)
      })
      .catch((error) => {
        setTableLoading(false)
      })
  }

  useEffect(() => {
    checkClearBtn()

    return getData()
  }, [lazyState])

  useEffect(() => initLazyState(), [])

  // Handles table page change
  const onPage = (event) => {
    setLazyState((prev) => applyPageEvent(prev, event))
  }

  // Handles table sorting
  const onSort = (event) => {
    setLazyState(event)
  }

  // Handles table filtring
  const onFilter = (event) => {
    setLazyState(event)
  }

  // Enable and disable clear button according to filtring and sorting
  const checkClearBtn = () => {
    if (lazyState?.sortField || lazyState?.sortOrder) {
      setClearBtnDisabled(false)
      return
    }
    for (const key in lazyState?.filters) {
      if (lazyState?.filters[key].value) {
        setClearBtnDisabled(false)
        return
      }
    }
    setClearBtnDisabled(true)
  }

  // Filter TEMPLATES
  const manufacturerNameFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      placeholder='Manufacturer name'
    />
  )

  // (Edit - Delete) column body template
  const actionBodyTemplate = (rowData) => {
    return (
      <React.Fragment>
        <div className='d-flex justify-content-center gap-2'>
          <Button
            icon='pi pi-pencil'
            rounded
            outlined
            className='mr-2'
            onClick={() => AddEditRef.current.editManufacturer(rowData)}
          />

          <Button
            icon='pi pi-trash'
            rounded
            outlined
            severity='danger'
            onClick={() => DeleteRef.current.confirmDeleteManufacturer(rowData)}
          />
        </div>
      </React.Fragment>
    )
  }
  const statusTemplate = (rowData) => {
    return (
      <>
        <span>{rowData.comingSoon ? 'Coming Soon' : 'Available'}</span>
      </>
    )
  }
  const systemTemplate = (rowData) => {
    // Handle both single system and array of systems
    const systems = Array.isArray(rowData.system)
      ? rowData.system
      : [rowData.system]

    return (
      <div className='d-flex  flex-column align-items-center justify-content-center gap-2 flex-wrap'>
        {systems.map((system, index) => (
          <div
            key={index}
            className='bg-primary text-white px-2 py-2 rounded-pill '
          >
            {system}
          </div>
        ))}
      </div>
    )
  }

  const visibilityTemplate = (rowData) => {
    const labels = {
      exclusive: 'Exclusive',
      restricted: 'Origins Management only',
    }
    const label = labels[rowData.visibility]

    // Public manufacturers are the norm, so only flag the restricted ones.
    return label ? (
      <div className='d-flex align-items-center justify-content-center'>
        <div className='bg-primary text-white px-2 py-2 rounded-pill'>
          {label}
        </div>
      </div>
    ) : (
      <span>All pharmacies</span>
    )
  }

  const shippingFeeBody = (rowData) => {
    return <span>{formatCurrency(rowData.shippingFee)}</span>
  }
  // Toolbar body templates
  const topLeftToolbarTemplate = () => {
    return (
      <div className='d-flex'>
        <Button
          type='button'
          icon='pi pi-filter-slash'
          label='Clear'
          outlined
          onClick={initLazyState}
          disabled={clearBtnDisabled}
        />
      </div>
    )
  }

  const topRightToolbarTemplate = () => {
    return (
      <div className='d-flex'>
        <Button
          label='New Manufacturer'
          icon='pi pi-plus'
          severity='success'
          onClick={() => AddEditRef.current.openNew()}
        />
      </div>
    )
  }

  return (
    <>
      <div className='card flex justify-content-center'>
        <Toast ref={toast} />
      </div>

      <MainContentLayout
        title='Manufacturers'
        dt={exportCSV}
        handleExport={true}
        loading={csvLoading}
      >
        <div className='card'>
          <Toolbar
            className='mb-2'
            left={topLeftToolbarTemplate}
            right={topRightToolbarTemplate}
          />

          <DataTable
            ref={dt}
            value={manufacturers}
            dataKey='id'
            paginator
            lazy
            showGridlines
            removableSort
            rowsPerPageOptions={rowsPerPageOptions}
            totalRecords={totalRecords}
            loading={tableLoading}
            globalFilterFields={['name']}
            emptyMessage='No manufacturers found.'
            filters={lazyState?.filters}
            sortField={lazyState?.sortField}
            sortOrder={lazyState?.sortOrder}
            rows={lazyState?.rows}
            first={lazyState?.first}
            onPage={onPage}
            onSort={onSort}
            onFilter={onFilter}
            onRowClick={(e) => AddEditRef.current.editManufacturer(e.data)}
            selectionMode='single'
            selection={selectedMolecule}
            onSelectionChange={(e) => setSelectedMolecule(e.value)}
          >
            <Column
              field='name'
              header='Name'
              sortable
              style={{
                minWidth: '16rem',
                position: 'sticky',
                left: 0,
                background: 'white',
                zIndex: 1,
              }}
              filterPlaceholder='Filter by Name'
              filter
              filterElement={manufacturerNameFilterElement}
              showFilterMatchModes={false}
              showFilterOperator={false}
              showAddButton={false}
              frozen
            ></Column>
            <Column
              field='gpIsOrdable'
              header='Direct Orders'
              style={{ minWidth: '16rem' }}
              body={(rowData) => (rowData.gpIsOrdable ? 'Yes' : 'No')}
              showFilterMatchModes={false}
              showFilterOperator={false}
              showAddButton={false}
            ></Column>
            <Column
              field='email'
              header='Email'
              style={{ minWidth: '12rem' }}
              showFilterMatchModes={false}
              showFilterOperator={false}
              showAddButton={false}
            ></Column>
            <Column
              field='gpMinOrderPrice'
              header='Min Order Price'
              style={{ minWidth: '12rem' }}
              showFilterMatchModes={false}
              showFilterOperator={false}
              showAddButton={false}
            ></Column>
            <Column
              field='leadTime'
              header='Lead Time'
              style={{ minWidth: '12rem' }}
              showFilterMatchModes={false}
              showFilterOperator={false}
              showAddButton={false}
            ></Column>
            <Column
              field='cutOffTime'
              header='Cut Off Time'
              style={{ minWidth: '12rem' }}
              showFilterMatchModes={false}
              showFilterOperator={false}
              showAddButton={false}
            ></Column>
            <Column
              field='comingSoon'
              header='Status'
              style={{ minWidth: '12rem' }}
              showFilterMatchModes={false}
              showFilterOperator={false}
              showAddButton={false}
              body={statusTemplate}
            ></Column>
            <Column
              field='system'
              header='System'
              style={{ minWidth: '20rem' }}
              showFilterMatchModes={false}
              showFilterOperator={false}
              showAddButton={false}
              body={systemTemplate}
            ></Column>
            <Column
              field='visibility'
              header='Availability'
              style={{ minWidth: '14rem' }}
              showFilterMatchModes={false}
              showFilterOperator={false}
              showAddButton={false}
              body={visibilityTemplate}
            ></Column>

            <Column
              field='shippingFee'
              header='Shipping Fee'
              style={{ minWidth: '12rem' }}
              showFilterMatchModes={false}
              showFilterOperator={false}
              showAddButton={false}
              body={shippingFeeBody}
            ></Column>
            <Column
              body={actionBodyTemplate}
              exportable={false}
              style={{ minWidth: '12rem' }}
            ></Column>
          </DataTable>

          <ModifyManufacture ref={refs} getData={getData} />
        </div>
      </MainContentLayout>
    </>
  )
}
